/* eslint-disable camelcase */
import {
  convertNumToCurrencyStr,
  convertRowToCSV,
  formatAmountsWithCommas,
  formatDate,
} from '../../../utils/globalHelpers';
import {
  AMORTIZATION_SCHEDULE_COLUMNS,
  DECIMAL_ERROR,
  INTEREST_RATE_ERROR,
  PAYMENT_LIMIT_ERROR,
  REASON_CODE_DICTIONARY,
  SCHEDULE_HEADERS,
  VALUE_ERROR,
} from './constants';

export const formatScheduledPayments = ({
  cumulativeInterestAmount,
  cumulativePrincipalAmount,
  endingBalanceAmount,
  monthlyInterestAmount,
  monthlyPrincipalAmount,
  paymentDate,
  paymentNumber,
  startingBalanceAmount,
}) => ({
  cumulativeInterest: formatAmountsWithCommas(cumulativeInterestAmount),
  cumulativePrincipal: formatAmountsWithCommas(cumulativePrincipalAmount),
  endingBalance: formatAmountsWithCommas(endingBalanceAmount),
  interestPayment: formatAmountsWithCommas(monthlyInterestAmount),
  paymentDate: formatDate(paymentDate, 'UTC', 'l'),
  paymentNumber,
  principalPayment: formatAmountsWithCommas(monthlyPrincipalAmount),
  startingBalance: formatAmountsWithCommas(startingBalanceAmount),
});

export const parseScheduleReasons = (reasons) => {
  const error = { validationErrors: {} };
  return reasons.reduce((acc, reasonCode) => {
    const { key, message } = REASON_CODE_DICTIONARY[reasonCode];
    if (key !== 'serviceError') {
      error.validationErrors[key] = { message };
    } else {
      error.message = message;
    }
    return acc;
  }, error);
};

export const chartAxisFormatter = (num) => {
  if (num >= 1000000000) {
    return `${(num / 1000000000).toFixed(1).replace(/\.0$/, '')}G`;
  }
  if (num >= 1000000) {
    return `${(num / 1000000).toFixed(1).replace(/\.0$/, '')}M`;
  }
  if (num >= 1000) {
    return `${(num / 1000).toFixed(1).replace(/\.0$/, '')}K`;
  }
  return num;
};

export const convertScheduledPaymentsToCSVString = (schedule, formatMessage) => {
  // Convert the CSV file column header to the internationalized values
  const columnHeaders = SCHEDULE_HEADERS.map((header) => {
    const { intlId } = AMORTIZATION_SCHEDULE_COLUMNS.find((column) => column.value === header);
    return formatMessage({ id: intlId });
  });

  return schedule.reduce((acc, record) => (
    acc + convertRowToCSV(SCHEDULE_HEADERS, record)
  ), `${convertRowToCSV(columnHeaders)}`);
};

export const generateChartData = ({ data, linesToChart }) => {
  const result = {};
  data.forEach((item) => {
    linesToChart.forEach(({ coordinates: { x, y }, name }) => {
      const resultCoordinates = { x: item[x], y: item[y] };
      if (!result[name]) {
        result[name] = [resultCoordinates];
      } else {
        result[name].push(resultCoordinates);
      }
    });
  });
  return result;
};

export const isValidInputsFunc = (dispatchUpdateFormError, formValues) => {
  const form = 'amortization';
  let isValid = true;
  let field;
  const {
    amount,
    interestRate,
    monthlyPaymentAmount,
    numberOfPayments,
    paymentType,
    principalBalance,
    valueType,
  } = formValues;
  /* eslint-disable max-len */
  const minimumMonthlyPayment = Number((Number(principalBalance) * (Number(interestRate) / 1200) + 0.01).toFixed(2));
  const minMonthlyPaymentError =
    `Monthly payment amount should be greater than or equal to ${convertNumToCurrencyStr(minimumMonthlyPayment)}.`;
    /* eslint-enable max-len */
  if (
    valueType === 'Monthly Payment Amount' &&
    Number(monthlyPaymentAmount) < minimumMonthlyPayment
  ) {
    isValid = false;
    field = 'monthlyPaymentAmount';
    dispatchUpdateFormError({ field, form, value: minMonthlyPaymentError });
  }
  if (valueType === 'Number of Months' && Number(numberOfPayments) <= 0) {
    isValid = false;
    field = 'numberOfPayments';
    dispatchUpdateFormError({ field, form, value: VALUE_ERROR });
  }
  if (Number(principalBalance) <= 0) {
    isValid = false;
    field = 'principalBalance';
    dispatchUpdateFormError({ field, form, value: VALUE_ERROR });
  }
  if (paymentType !== 'Optional' && Number(amount) <= 0) {
    isValid = false;
    field = 'amount';
    dispatchUpdateFormError({ field, form, value: VALUE_ERROR });
  }
  if (Number(interestRate) <= 0) {
    isValid = false;
    field = 'interestRate';
    dispatchUpdateFormError({ field, form, value: VALUE_ERROR });
  }
  if (paymentType !== 'Optional' && Number(amount) > Number(principalBalance)) {
    isValid = false;
    field = 'amount';
    dispatchUpdateFormError({ field, form, value: PAYMENT_LIMIT_ERROR });
  }
  if (
    valueType === 'Monthly Payment Amount' &&
    Number(monthlyPaymentAmount) > Number(principalBalance)
  ) {
    isValid = false;
    field = 'monthlyPaymentAmount';
    dispatchUpdateFormError({ field, form, value: PAYMENT_LIMIT_ERROR });
  }
  if (!isValidDecimalLength(interestRate, 3)) {
    isValid = false;
    field = 'interestRate';
    dispatchUpdateFormError({ field, form, value: INTEREST_RATE_ERROR });
  }
  if (!isValidDecimalLength(amount)) {
    isValid = false;
    field = 'amount';
    dispatchUpdateFormError({ field, form, value: DECIMAL_ERROR });
  }
  if (!isValidDecimalLength(monthlyPaymentAmount)) {
    isValid = false;
    field = 'monthlyPaymentAmount';
    dispatchUpdateFormError({ field, form, value: DECIMAL_ERROR });
  }
  if (!isValidDecimalLength(principalBalance)) {
    isValid = false;
    field = 'principalBalance';
    dispatchUpdateFormError({ field, form, value: DECIMAL_ERROR });
  }
  return isValid;
};

export const isValidDecimalLength = (amount, validDecimalLength = 2) => {
  const amountDecimal = amount.split('.')[1];
  return !(amountDecimal && amountDecimal.length > validDecimalLength);
};

export const convertNumToCustomCurrencyStr = (number, digits = 2) => {
  const strNum = number.toFixed(digits).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return `${strNum}`;
};

export const convertCustomCurrencyStrToNum = (currencyStr) => (
  Number(currencyStr.replace(/[^0-9.-]+/g, ''))
);
